"use client";

import { SIDEBAR_WIDTH, appConfig } from "@/constants";
import type { ExperimentVariantValue } from "@/hooks/useFeatureFlags";
import { useTopHeroScrollMotion } from "@/hooks/useTopHeroScrollMotion";
import { EnvironmentNames } from "@/types";
import { mapToQuestGroup } from "@/utils/cms";
import { getTitleVariant } from "@/utils/cms/getContentVariants";
import type { GameSpecifications } from "@/utils/cms/getGameSpecs";
import {
  Accordion,
  Body,
  Box,
  Divider,
  HeroBackground,
  Stack,
  mediaQuerySizesHelper,
  useIntersectionObserver,
  useTheme,
} from "@biom3/react";
import { SliceContext } from "@cms/context";
import { components } from "@cms/slices";
import { getWebLink } from "@cms/utils";
import {
  ARMOURX_GAME_UID,
  ARMOURX_TWITCH_DROPS_DATE,
  ARMOURX_TWITCH_DROPS_END_DATE,
  ELUMIA_GAME_UID,
  ELUMIA_TWITCH_DROPS_DATE,
  ELUMIA_TWITCH_DROPS_END_DATE,
  METATOY_GAME_UID,
  METATOY_TWITCH_DROPS_DATE,
  METATOY_TWITCH_DROPS_END_DATE,
} from "@components/layout/GameDropsLayout";
import type { Content } from "@prismicio/client";
import { SliceZone } from "@prismicio/react";
import type { WishlistProps } from "@ui-kit";
import { Specs } from "@ui-kit/GameSpecs/Specs";
import { PremiumGameHeader } from "@ui-kit/PremiumGameHeader";
import { UnsubscribeFooter } from "@ui-kit/Unsubscribe";
import {
  useIsLessThanMediumScreen,
  useIsLessThanXXXLargeScreen,
} from "@ui-kit/hooks/screenSizeHooks";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useMemo, useRef, useState } from "react";
import { QuestDrawerContainer } from "../QuestDrawer";
import { QuestsPanel } from "../QuestsPanel";
import { useCampaign } from "../QuestsV2/CampaignProvider";
import { PointsCampaign } from "../QuestsV2/Points/PointsCampaign";

type PremiumGameDetailsLayoutProps = {
  defaultBgImage: string | null | undefined;
  game: Content.GameDocument;
  page: Content.PremiumGameDetailsPageDocument;
  gameSpecs: GameSpecifications;
  wishlistProps: WishlistProps;
  questsGroups:
    | Content.OnboardingQuestGroupDocument<string>[]
    | null
    | undefined;
};

function UnwrappedPremiumGameDetailsLayoutVariantC({
  defaultBgImage,
  game,
  page,
  gameSpecs,
  wishlistProps,
  questsGroups,
}: PremiumGameDetailsLayoutProps) {
  const { visibleCampaignContent } = useCampaign();
  // DO NOT REMOVE THIS LINE
  // This is a mock for a running content variant experiment
  const [placeholderContentVariant] = useState<ExperimentVariantValue>();

  const mappedQuests = questsGroups
    ? mapToQuestGroup(game.uid, questsGroups)
    : [];

  const { base } = useTheme();
  const gameContext = useMemo(() => ({ game }), [game]);
  const [isQuestDrawerExpanded, setIsQuestDrawerExpanded] = useState(true);

  const [backgroundImg, setBackgroundImg] = useState<string | null | undefined>(
    defaultBgImage,
  );
  const mediaCarousel = page.data.slices.find(
    (slice) => slice.slice_type === "media_carousel",
  );
  const anyVideoEmbed = page.data.slices.find(
    (slice) => slice.slice_type === "any_video_embed",
  );
  const contentBox = page.data.slices.find(
    (slice) => slice.slice_type === "content_box",
  );
  const otherSlices = page.data.slices.filter(
    (slice) =>
      slice.slice_type !== "any_video_embed" &&
      slice.slice_type !== "media_carousel" &&
      slice.slice_type !== "content_box",
  );

  const playLink = getWebLink(game.data.website).url;
  const pageTitle =
    getTitleVariant(page, placeholderContentVariant) || game.data.name || "";
  const heroImageRef = useRef<HTMLImageElement | null>(null);
  const { isIntersecting } = useIntersectionObserver();
  useTopHeroScrollMotion({
    heroImageRef,
    isIntersecting,
  });

  const displayCampaignContent = useMemo(() => {
    const isProduction = appConfig.ENVIRONMENT === EnvironmentNames.PRODUCTION;
    const campaignEndDate =
      visibleCampaignContent &&
      new Date(
        visibleCampaignContent.campaign.endDate.getTime() +
          7 * 24 * 60 * 60 * 1000,
      );
    const isCampaignActive = campaignEndDate && campaignEndDate > new Date();
    const standardQuestsAvailable =
      mappedQuests?.length && mappedQuests?.length > 0;

    // if prod, only display campaign content if there is a campaign and no standard quests available
    if (isProduction) {
      return isCampaignActive && !standardQuestsAvailable;
    }

    // if non-prod, campaign content should take precedence over standard quests
    // this is to allow for testing of campaign content in non-prod environments
    return isCampaignActive;
  }, [visibleCampaignContent, mappedQuests]);

  const twitchDropsLive = useMemo(() => {
    const now = new Date();
    return (
      (game.uid === ELUMIA_GAME_UID &&
        now >= ELUMIA_TWITCH_DROPS_DATE &&
        now <= ELUMIA_TWITCH_DROPS_END_DATE) ||
      (game.uid === ARMOURX_GAME_UID &&
        now >= ARMOURX_TWITCH_DROPS_DATE &&
        now <= ARMOURX_TWITCH_DROPS_END_DATE) ||
      (game.uid === METATOY_GAME_UID &&
        now >= METATOY_TWITCH_DROPS_DATE &&
        now <= METATOY_TWITCH_DROPS_END_DATE)
    );
  }, [game.uid]);

  const isSmallScreenMode = useIsLessThanMediumScreen();
  const isXXLargeScreenMode = useIsLessThanXXXLargeScreen();
  return (
    <>
      {backgroundImg && (
        <HeroBackground
          sx={{
            maskImage: "linear-gradient(to right, black 70%, transparent 100%)",
            pos: "absolute",
            top: "0",
            left: "0",
            bradtl: "base.borderRadius.x8",
            bradtr: "base.borderRadius.x8",
            w: { default: "100%", xLarge: "66%" },
          }}
          aspectRatio={{ default: "1", xLarge: "16/11" }}
        >
          <HeroBackground.Image
            loading="eager"
            use={
              <Image
                src={backgroundImg}
                alt={backgroundImg}
                fill
                priority
                sizes={mediaQuerySizesHelper([
                  { rule: "default", width: "100dvw - 16px" },
                  {
                    rule: "minWidth",
                    breakpoint: "medium",
                    width: `calc(100dvw - ${SIDEBAR_WIDTH} - 32px)`,
                  },
                ])({ base })}
              />
            }
          />
        </HeroBackground>
      )}
      <Stack
        sx={{ pos: "relative" }}
        testId="GameDetailPage__mainContainer"
        gap={{ default: "base.spacing.x2", medium: "base.spacing.x8" }}
      >
        <PremiumGameHeader
          playLink={playLink}
          game={game}
          wishlistProps={wishlistProps}
          stickyPageTitle={pageTitle}
          useMobileExperiment={isSmallScreenMode}
          showDivider={false}
        />
        {isSmallScreenMode && (
          <Stack
            sx={{ w: { default: "100%", xLarge: "72%" } }}
            testId="heroSection__leftSide"
          >
            <SliceContext.Provider
              value={{
                t: (key: string) => key,
                wishlistProps,
                data: {
                  game,
                  mediaCarousel: {
                    onSlideChange: (url: string) => setBackgroundImg(url),
                  },
                },
              }}
            >
              <SliceZone
                components={components}
                slices={[
                  ...(mediaCarousel ? [mediaCarousel] : []),
                  ...(anyVideoEmbed ? [anyVideoEmbed] : []),
                ]}
                context={gameContext}
              />
            </SliceContext.Provider>
          </Stack>
        )}

        <Stack
          sx={{ px: { default: "none", large: "base.spacing.x12" } }}
          testId="heroSection"
          direction={{ default: "column", medium: "row" }}
          alignItems="stretch"
          gap={{ default: "base.spacing.x8", large: "base.spacing.x12" }}
        >
          <Stack
            sx={{ w: { default: "100%", xLarge: "66%", flexShrink: 0 } }}
            testId="heroSection__leftSide"
          >
            <SliceContext.Provider
              value={{
                t: (key: string) => key,
                wishlistProps,
                data: {
                  game,
                  mediaCarousel: {
                    onSlideChange: (url: string) => setBackgroundImg(url),
                  },
                },
              }}
            >
              <SliceZone
                components={components}
                slices={
                  isSmallScreenMode
                    ? [...(contentBox ? [contentBox] : [])]
                    : [
                        ...(mediaCarousel ? [mediaCarousel] : []),
                        ...(anyVideoEmbed ? [anyVideoEmbed] : []),
                        ...(contentBox ? [contentBox] : []),
                      ]
                }
                context={gameContext}
              />
            </SliceContext.Provider>

            {game.uid ===
            "immutable-play-onboarding" ? null : isSmallScreenMode ? (
              <Accordion chevronSide="right">
                <Accordion.TargetLeftSlot sx={{ display: "flex" }}>
                  <Body size="medium" color="secondary">
                    Specifications
                  </Body>
                  <Divider size={"xSmall"} sx={{ mt: "base.spacing.x8" }} />
                </Accordion.TargetLeftSlot>
                <Accordion.ExpandedContent>
                  <Specs
                    gameSpecifications={gameSpecs.specifications}
                    game={game}
                  />
                </Accordion.ExpandedContent>
              </Accordion>
            ) : (
              <>
                <Divider size={"xSmall"} sx={{ mt: "base.spacing.x8" }} />
                <Specs
                  gameSpecifications={gameSpecs.specifications}
                  game={game}
                />
              </>
            )}

            <Box testId="contentSection">
              <SliceContext.Provider
                value={{
                  t: (key: string) => key,
                  wishlistProps,
                  data: {
                    game,
                    mediaCarousel: {
                      onSlideChange: (url: string) => setBackgroundImg(url),
                    },
                  },
                }}
              >
                <SliceZone
                  components={components}
                  slices={otherSlices}
                  context={gameContext}
                />
              </SliceContext.Provider>
            </Box>
          </Stack>

          {!isXXLargeScreenMode && (
            <Box
              sx={{
                w: {
                  default: "100%",
                  medium: "34%",
                  flexShrink: 0,
                },
                maxw: "770px",
              }}
              testId="heroSection__rightSide"
            >
              {displayCampaignContent && visibleCampaignContent ? (
                <PointsCampaign
                  game={game}
                  wishlistProps={wishlistProps}
                  visibleCampaignContent={visibleCampaignContent}
                />
              ) : (
                <QuestsPanel
                  wishlistProps={wishlistProps}
                  game={game}
                  questsGroups={mappedQuests}
                  twitchDropsGameUid={twitchDropsLive ? game.uid : undefined}
                />
              )}
            </Box>
          )}
        </Stack>
      </Stack>
      {isXXLargeScreenMode && (
        <QuestDrawerContainer
          wishlistProps={wishlistProps}
          useMobileLayoutVariant={isSmallScreenMode}
          game={game}
          isExpanded={isQuestDrawerExpanded}
          onExpandedChange={setIsQuestDrawerExpanded}
          twitchDropsGameUid={twitchDropsLive ? game.uid : undefined}
        >
          {displayCampaignContent && visibleCampaignContent ? (
            <PointsCampaign
              game={game}
              wishlistProps={wishlistProps}
              visibleCampaignContent={visibleCampaignContent}
            />
          ) : (
            <QuestsPanel
              showHeader={false}
              wishlistProps={wishlistProps}
              game={game}
              questsGroups={mappedQuests}
            />
          )}
        </QuestDrawerContainer>
      )}

      <Box sx={{ pt: "base.spacing.x6" }}>
        <UnsubscribeFooter />
      </Box>
    </>
  );
}

export const PremiumGameDetailsLayout = dynamic(
  () => Promise.resolve(UnwrappedPremiumGameDetailsLayoutVariantC),
  { ssr: false },
);
